// Utilities
import { defineStore } from 'pinia'
import { Item } from '@/types/item'
import { UserRole } from '@/types/roles'
import Tenant from '@/types/tenant'
import type { UserLocalStorageObject } from '@/types/user'
import type { HomeBlock } from '@/domain/home/HomeBlock'
import { stableSignedUrl } from '@/utils/signedUrl'

const CONFIG_ROLES = [UserRole.STAFF, UserRole.SUPER_ADMIN, UserRole.ADMIN] as const
const OPERATIONS_ROLES = [UserRole.STAFF, UserRole.SUPER_ADMIN, UserRole.OPERATIONAL_ADMIN] as const

export const useUserStore = defineStore('user', {
  state: (): UserLocalStorageObject => ({
    id: null,
    email: "",
    name: "",
    role: "",
    area: undefined as Item | undefined,
    profilePicture: "",
    approvalLevel: 1,
    tenants: [] as Tenant[],
    authMethod: undefined,
    cognitoGroups: [] as string[],
    language: null as string | null,
    homeBlocks: [] as HomeBlock[],
  }),
  getters: {
    getEmail: (state) => state.email,
    getName: (state) => state.name,
    getRole: (state) => state.role,
    getArea: (state) => state.area,
    getProfilePicture: (state) => state.profilePicture,
    getTenants: (state) => state.tenants,
    getApprovalLevel: (state) => state.approvalLevel,
    getAuthMethod: (state) => state.authMethod,
    getUser: (state) => ({ ...state }),
    canManageConfig: (state): boolean => (CONFIG_ROLES as readonly string[]).includes(state.role),
    canAccessOperations: (state): boolean => (OPERATIONS_ROLES as readonly string[]).includes(state.role),
    isApprover: (state): boolean => state.role === UserRole.APPROVER,
    canApproveProviders: (state): boolean => state.role === UserRole.APPROVER,
  },
  actions: {
    setUser(user: UserLocalStorageObject) {
      this.id = user.id ?? null;
      this.email = user.email;
      this.name = user.name;
      this.role = user.role;
      this.area = user.area;
      this.profilePicture = stableSignedUrl(this.profilePicture, user.profilePicture);
      this.tenants = user.tenants;
      this.approvalLevel = user.approvalLevel;
      this.authMethod = user.authMethod;
      this.language = user.language ?? null;
      if (user.cognitoGroups !== undefined) this.cognitoGroups = user.cognitoGroups;
    },
    setEmail(email: string) {
      this.email = email;
    },
    setName(name: string) {
      this.name = name;
    },
    setRole(role: UserRole | '') {
      this.role = role;
    },
    setArea(area: Item) {
      this.area = area;
    },
    setProfilePicture(profilePicture: string) {
      this.profilePicture = profilePicture;
    },
    setTenants(tenants: Tenant[]) {
      this.tenants = tenants;
    },
    setApprovalLevel(approvalLevel: number) {
      this.approvalLevel = approvalLevel;
    },
    setAuthMethod(authMethod: 'auth0' | 'cognito' | undefined) {
      this.authMethod = authMethod;
    },
    setCognitoGroups(groups: string[]) {
      this.cognitoGroups = groups;
    },
    clearUser() {
      this.id = null;
      this.homeBlocks = [];
      this.email = '';
      this.name = '';
      this.role = '';
      this.area = undefined;
      this.profilePicture = '';
      this.approvalLevel = 1;
      this.tenants = [];
      this.authMethod = undefined;
      this.cognitoGroups = [];
    },
  },
  persist: true
})
